/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2026 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import { Blob as DirectUploadBlob, DirectUpload } from "@rails/activestorage";
import { AudioBufferState } from "constants/AudioBufferState";
import { BlockTypes } from "constants/BlockTypes";
import { ClipUploadStates } from "constants/ClipUploadStates";
import { createSelector } from "reselect";
import { AppState } from "types/AppState";
import {
  Asset,
  AssetParticipant,
  AssetSet,
  AssetTranscript,
  AssetTranscriptSegment,
  AssetTranscriptWord,
} from "types/Asset";
import { AssetId, MediaType } from "@phonos/types";
import { Clip, ClipId, isClipWithAsset, PlaylistClip } from "types/Clip";
import { ParticipantSet } from "types/Participant";
import { Project, ProjectId } from "types/Project";
import {
  RecordingSession,
  RecordingSessionId,
  RecordingSessionTrack,
  RecordingSessionTrackId,
} from "types/RecordingSession";
import { mapFromServerFormat, mapToServerFormat } from "utilities/ApiUtilities";
import { mapClipsToPlaylist, rangesIntersect } from "utilities/ClipUtilities";
import gatewayFetch from "utilities/gatewayFetch";
import { v4 as uuidv4 } from "uuid";
import { ItemWithId } from "types/ItemWithId";
import { AdobeId } from "reducers/CurrentUserReducer";
import { NewRelicEvent } from "constants/Analytics";
import getValidAccessToken from "utilities/getValidAccessToken";
import { maybeApply } from "utilities/FunctionUtilities";
import { TranscriptionService } from "constants/TranscriptionService";
import { UnexpectedHttpResponseError } from "types/UnexpectedHttpResponseError";
import { SelectionState } from "reducers/SelectionReducer";
import { ModelVersion } from "components/EnhancedSpeech/useEnhanceSpeech/ModelVersion";
import { getPlaylistDuration } from "AudioPlayer/StreamingAudioPlayer/PlaylistUtilities";
import { useSelector } from "react-redux";

// This assigns a random identifier to each token in the transcript.
// Since a token can appear more than once in a block, the transcript token id
// will be combined a prefix from the clip to create a globally unique identifier.
// Unlike, the clip id, the clip prefix will not change under clip splits (from an
// insertion or deletion, TrimUtilities.ts) to keep the token ids stable across
// edits.
export const populateTranscript = (
  transcript: AssetTranscript,
  participants?: AssetParticipant[]
): AssetTranscript => {
  if (!transcript?.length) {
    return [];
  }

  return transcript.map((segment) => {
    // If we have a trackParticipant for this speaker, override the display name
    const unidentifiedSpeakerNumber = segment.unidentifiedSpeakerNumber;
    const participant = participants?.find(
      (participant) =>
        participant.unidentifiedSpeakerNumber === unidentifiedSpeakerNumber
    );
    if (participant?.displayName) {
      segment.displayName = participant.displayName;
      segment.displayNameIsOverridden = true;
    }

    return {
      ...segment,
      words: segment.words.map((word) => {
        return {
          ...word,
          id: uuidv4(),
        };
      }),
    };
  });
};

export const removeIdsFromTranscript = (
  transcript: AssetTranscript
): AssetTranscript => {
  return transcript.map(removeSegmentWordIds);
};

const removeSegmentWordIds = (
  segment: AssetTranscriptSegment
): AssetTranscriptSegment => {
  return {
    ...segment,
    words: segment.words.map(removeWordId),
  };
};

const removeWordId = (word: AssetTranscriptWord): AssetTranscriptWord => {
  return Object.fromEntries(
    Object.entries(word).filter(([k, v]) => {
      return k != "id";
    })
  ) as AssetTranscriptWord;
};

// TODO: Extracted the inline generic type. Should eventually type this fully...
type TrackFromServer = Record<string, any>;

export const mapTrackToAsset = (track: TrackFromServer): Asset => {
  const {
    id,
    mediaType,
    url,
    participants,
    streamingAudioV1Url,
    streamingAudioV2Url,
    streamingVideo360PUrl,
    waveformUrl,
    transcript,
    transcriptionState,
    startedTranscriptionAt,
    studioSoundTrackId,
    originalTrackId,
    enhancementErrorType,
    enhancementErrorMessage,
    hasAnyEnhancementErrors,
    transcriptionService,
    transcriptDetectedLanguage,
  } = mapFromServerFormat(track);

  const mappedParticipants =
    participants?.map((participant: AssetParticipant) => {
      // If we have no media (e.g. a single-Track with a Participant an overridden name), we need to add the media
      if (streamingVideo360PUrl && !participant.media) {
        participant.media = {
          id,
          mediaType: MediaType.VIDEO,
          streamingVideo360PUrl,
        };
      }

      return participant;
    }) || [];

  const transcriptWithIds =
    transcript !== undefined
      ? populateTranscript(transcript, mappedParticipants)
      : undefined;
  const mappedStartedTranscriptionAt = startedTranscriptionAt
    ? new Date(startedTranscriptionAt)
    : undefined;

  return {
    id,
    mediaType,
    url,
    participants: mappedParticipants,
    streamingAudioV1Url,
    streamingAudioV2Url,
    streamingVideo360PUrl,
    waveformUrl,
    transcript: transcriptWithIds,
    transcriptionState,
    startedTranscriptionAt: mappedStartedTranscriptionAt,
    studioSoundTrackId,
    originalTrackId,
    audioBufferState: AudioBufferState.PENDING,
    enhancementErrorType,
    enhancementErrorMessage,
    hasAnyEnhancementErrors,
    transcriptionService,
    transcriptDetectedLanguage,
  };
};

export function mapArrayToSet<U extends string, T extends ItemWithId<U>>(
  items: T[]
): Record<U, T> {
  return Object.fromEntries(items.map((item: T) => [item.id, item])) as Record<
    U,
    T
  >;
}

export const mapTracksToAssets = (tracks: Record<string, any>[]): AssetSet =>
  tracks.reduce((result, track) => {
    const asset = mapTrackToAsset(track);
    return {
      ...result,
      [track.id]: asset,
    };
  }, {});

export const mapBackendBlocksToClips = (
  blocks: Record<string, any>[]
): Clip[] =>
  blocks.map((block) => {
    const mappedBlock = mapFromServerFormat(block);

    let uploadState = mappedBlock.uploadState;

    if (
      mappedBlock.uploadState &&
      ![ClipUploadStates.COMPLETE].includes(mappedBlock.uploadState)
    ) {
      if (mappedBlock.assetId) {
        newrelic.addPageAction(
          NewRelicEvent.CLIP_RECOVERY_UPLOAD_STATE_UPDATED,
          {
            clipId: block.id,
            changedFrom: uploadState,
            changedTo: ClipUploadStates.COMPLETE,
          }
        );

        uploadState = ClipUploadStates.COMPLETE;
      }
    }

    if (mappedBlock.type === BlockTypes.TRANSCRIPTION) {
      mappedBlock.tokenIdPrefix = mappedBlock.id;
    }

    return {
      ...mappedBlock,
      isBackground: Boolean(mappedBlock.isBackground),
      fadeIn: Boolean(mappedBlock.fadeIn),
      fadeOut: Boolean(mappedBlock.fadeOut),
      uploadState: uploadState,
    } as Clip;
  });

export const getUsedAssets = createSelector(
  (state: AppState) => state.clips,
  (state) => state.assets,
  (clips, assets) => {
    return clips.reduce((usedAssets: AssetSet, clip) => {
      if (
        isClipWithAsset(clip) &&
        clip.assetId in assets &&
        !(clip.assetId in usedAssets)
      ) {
        const asset = assets[clip.assetId];
        usedAssets[clip.assetId] = asset;

        const enhancedId = asset.studioSoundTrackId;
        if (enhancedId && enhancedId in assets) {
          const enhancedAsset = assets[enhancedId];
          usedAssets[enhancedId] = enhancedAsset;
        }
      }
      return usedAssets;
    }, {});
  }
);

export const isCurrentUserOwner = createSelector(
  (state: AppState) => state.currentUser,
  (state) => state.project,
  (currentUser, project) =>
    currentUser !== null && currentUser.adobeId === project?.owner_id
);

export const useIsCurrentUserOwner = (): boolean => {
  const currentUserIsOwner = useSelector(isCurrentUserOwner);
  return currentUserIsOwner;
};

export const getProjectOwnerAdobeId = createSelector(
  (state: AppState) => state.project,
  (project) => project?.owner_id
);

export const isProjectRecording = createSelector(
  (state: AppState) => state.project,
  (project) => project?.is_recording
);

export const getFullPlaylist = createSelector(
  (state: AppState) => state.clips,
  (state) => state.assets,
  (state) => state.project?.filters,
  (state) => state.project?.enhance_speech_model_version,
  (clips, assets, filters, enhanceSpeechModelVersion) => {
    return mapClipsToPlaylist(
      clips,
      assets,
      filters,
      enhanceSpeechModelVersion
    );
  }
);

const filterVideoPlaylist = (
  assets: AssetSet,
  playlist: PlaylistClip[]
): PlaylistClip[] => {
  return playlist.filter((clip) => {
    const asset = assets[clip.assetId];
    return asset?.mediaType === MediaType.VIDEO;
  });
};

export const getDurationInMilliseconds = createSelector(
  getFullPlaylist,
  getPlaylistDuration
);

export const getPlaylistFromCurrentSelection = createSelector(
  getFullPlaylist,
  (state) => state.selection,
  extractPlaylistSelection
);

export const getPlaylist = (
  fromCurrentSelection: boolean = false
): typeof getPlaylistFromCurrentSelection | typeof getFullPlaylist => {
  return fromCurrentSelection
    ? getPlaylistFromCurrentSelection
    : getFullPlaylist;
};

export const getSelectedVideoPlaylist = createSelector(
  (state) => state.assets,
  getPlaylistFromCurrentSelection,
  filterVideoPlaylist
);

export const getFullVideoPlaylist = createSelector(
  (state) => state.assets,
  getFullPlaylist,
  filterVideoPlaylist
);

export const getVideoPlaylist = (
  fromCurrentSelection: boolean = false
): typeof getSelectedVideoPlaylist | typeof getFullVideoPlaylist => {
  return fromCurrentSelection ? getSelectedVideoPlaylist : getFullVideoPlaylist;
};

export function extractPlaylistSelection(
  playlist: PlaylistClip[],
  selection: SelectionState
): PlaylistClip[] {
  const selectionStartTime = selection.startTime;
  const selectionEndTime = selection.endTime;

  // Return full playlist if no valid selection
  if (selectionStartTime == null || selectionEndTime == null) {
    return playlist;
  }

  // Find clips that intersect with the selection
  const clipsInSelection = playlist.filter((clip) => {
    const isRelevantType = [
      BlockTypes.WAVEFORM,
      BlockTypes.TRANSCRIPTION,
      BlockTypes.RECORDING_SESSION,
    ].includes(clip.type);

    if (!isRelevantType) {
      return false;
    }

    return rangesIntersect(clip, {
      startTime: selectionStartTime,
      endTime: selectionEndTime,
    });
  });

  if (clipsInSelection.length === 0) {
    return [];
  }

  // Munge clips to have required properties
  const isomorphicClips = clipsInSelection.map((clip) => {
    return {
      fadeIn: false,
      fadeOut: false,
      isBackground: false,
      ...clip,
    };
  });

  // Trim clips that are partially in the selection
  const trimmedPlaylist = isomorphicClips.map((clip) => {
    const needsStartTrim = clip.startTime < selectionStartTime;
    const needsEndTrim = clip.endTime > selectionEndTime;

    if (needsStartTrim || needsEndTrim) {
      // Calculate new boundaries based on the selection
      const newStartTime = needsStartTrim
        ? selection.startTime!
        : clip.startTime;
      const newEndTime = needsEndTrim ? selectionEndTime : clip.endTime;

      // Calculate new duration and offset
      const newDuration = newEndTime - newStartTime;
      const newOffset = needsStartTrim
        ? clip.offset + (selectionStartTime - clip.startTime)
        : clip.offset;

      // Create a new clip with a new ID and the trimmed boundaries
      // but maintain all other properties from the original clip
      return {
        ...clip,
        id: uuidv4(),
        duration: newDuration,
        offset: newOffset,
        startTime: newStartTime,
        endTime: newEndTime,
      };
    }

    return clip;
  });

  // Shift playlist to start at 0
  return trimmedPlaylist.map((clip) => ({
    ...clip,
    startTime: clip.startTime - selectionStartTime,
    endTime: clip.endTime - selectionStartTime,
  }));
}

export const leaveProject = async (project: Project): Promise<void> => {
  if (project && project.id) {
    await gatewayFetch(`/api/v1/projects/${project.id}/leave`, {
      method: "POST",
      keepalive: true,
    });
  }
};

export const setName = async ({
  project,
  name,
}: {
  project: Project;
  name: string;
}): Promise<Response> =>
  await gatewayFetch(`/api/v1/projects/${project.id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ project: { name } }),
  });

export const setEnhanceSpeechModelVersion = async ({
  project,
  enhanceSpeechModelVersion,
}: {
  project: Project;
  enhanceSpeechModelVersion: ModelVersion;
}): Promise<Response> =>
  await gatewayFetch(`/api/v1/projects/${project.id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      project: { enhance_speech_model_version: enhanceSpeechModelVersion },
    }),
  });

export class UploadError extends Error {
  constructor(private originalError: Error) {
    super("Error during direct upload");
  }

  get originalMessage(): string {
    return typeof this.originalError === "string"
      ? this.originalError
      : this.originalError.message || String(this.originalError);
  }
}

interface DirectUploadArgs {
  fileOrBlob: File;
  progressCallback?: (percentComplete: number) => void;
  folderPath: string;

  // this is a way to validate that the user is allowed to upload data. authenticated users are always allowed to
  // upload but anonymous users will only be able to upload into specific recording sessions that they are members of
  // and are in progress.
  uploadContext?: string;
}

export const directUpload = async ({
  fileOrBlob,
  progressCallback,
  folderPath,
  uploadContext,
}: DirectUploadArgs): Promise<DirectUploadBlob> => {
  const uploadUrl = `${process.env.PHONOS_GATEWAY_URL}/rails/active_storage/direct_uploads`;
  const accessToken = await getValidAccessToken();
  let xhr: XMLHttpRequest | undefined;
  const upload = new DirectUpload(fileOrBlob, uploadUrl, {
    directUploadWillCreateBlobWithXHR: (xhr): void => {
      xhr.setRequestHeader("Authorization", `Bearer ${accessToken?.token}`);
      xhr.setRequestHeader("X-Api-Key", process.env.ADOBE_IO_API_KEY ?? "");
      xhr.setRequestHeader("X-Folder-Path", folderPath);

      if (uploadContext) {
        xhr.setRequestHeader("X-Upload-Context", uploadContext);
      }
    },
    directUploadWillStoreFileWithXHR: (request): void => {
      xhr = request;
      request.upload.addEventListener("progress", (event) => {
        if (progressCallback) {
          progressCallback(event.loaded / event.total);
        }
      });
    },
  });

  return await new Promise((resolve, reject) => {
    upload.create(async (error, blob) => {
      if (error) {
        let uploadError: UploadError = new UploadError(error);
        // Special case: If the upload is for a recording session and the session is already finalized
        // We will have an HTTP status of 410 Gone.
        if (
          typeof error === "string" &&
          (error as string).includes("Status: 410")
        ) {
          uploadError = new DirectUploadGoneError(error as any);
        }
        newrelic.noticeError(uploadError, {
          originalError: uploadError.originalMessage,
          status: xhr?.status ?? -1,
          url: maybeApply(xhr?.responseURL, sanitizeSignedUrl) ?? "",
          unloaded: String(window.unloaded),
        });
        reject(uploadError);
      } else {
        resolve(blob);
      }
    });
  });
};

export const sanitizeSignedUrl = (url: string): string => {
  try {
    const decodedUrl = new URL(url);
    decodedUrl.searchParams.forEach((value, key) => {
      decodedUrl.searchParams.delete(key);
    });
    return decodedUrl.href;
  } catch {
    return url;
  }
};

interface UploadAssetArguments {
  project: Project;
  file: File;
  assetId: AssetId;
  duration: number;
  mediaType: MediaType | undefined;
  onProgress: (percentComplete: number) => void;
}

export async function uploadAsset({
  project,
  file,
  assetId,
  duration,
  mediaType,
  onProgress: progressCallback,
}: UploadAssetArguments): Promise<unknown> {
  const id = assetId ?? uuidv4();

  const uploadResponse = await directUpload({
    fileOrBlob: file,
    progressCallback,
    folderPath: S3Folders.ProjectTracks,
  });
  const signedFileId = uploadResponse.signed_id;
  const formData = new FormData();
  formData.append("signed_file_id", signedFileId);
  formData.append("track_id", id);
  formData.append("duration", duration.toString());
  if (mediaType) {
    formData.append("media_type", mediaType);
  }
  const rawResponse = await gatewayFetch(
    `/api/v1/projects/${project.id}/tracks`,
    {
      method: "POST",
      body: formData,
    }
  );

  return await rawResponse.json();
}

interface CreateRecordingSessionArguments {
  project: Project;
  clipId: ClipId;
  recordingSessionId: RecordingSessionId;
  participants: ParticipantSet;
  transcriptionService?: TranscriptionService;
}

export const createRecordingSession = async ({
  project,
  clipId,
  recordingSessionId,
  participants,
  transcriptionService,
}: CreateRecordingSessionArguments): Promise<RecordingSession> => {
  const participantsMapped = Object.values(participants).map((participant) => {
    return {
      adobe_id: participant.adobeId,
      display_name: participant.chosenName,
      is_anonymous: (participant.chosenName?.length ?? 0) > 0,
    };
  });
  const response = await gatewayFetch(
    `/api/v1/projects/${project.id}/recording_sessions`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        id: recordingSessionId,
        clip_id: clipId,
        participants: participantsMapped,
        transcription_service: transcriptionService,
      }),
    },
    { reportErrors: false }
  );

  if (response.status !== 201) {
    const error = new Error(
      `Creating Recording Session Failed. Status: ${response.status}`
    );

    throw error;
  }

  const recordingSessionJson = await response.json();
  return mapFromServerFormat(recordingSessionJson) as RecordingSession;
};

export const fetchRecordingSession = async ({
  projectId,
  recordingSessionId,
}: {
  projectId: ProjectId;
  recordingSessionId: RecordingSessionId;
}): Promise<RecordingSession> => {
  const response = await gatewayFetch(
    `/api/v1/projects/${projectId}/recording_sessions/${recordingSessionId}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      // Abort the request if we don't get a response within 30 seconds
      signal: AbortSignal.timeout(30000),
    },
    { reportErrors: false }
  );

  if (response.status === 200) {
    return mapFromServerFormat(await response.json()) as RecordingSession;
  } else {
    const error = new Error(
      `Fetching recording session failed. Status: ${response.status}`
    );

    throw error;
  }
};

// This type is a guest view of `RecordingSession`. Currently
// we don't need to access any properties off this, just need to know
// if one exists or not, hence the unknown type alias. Replace with
// an actual interface if we ever need to access the data.
export type ActiveRecordingSession = unknown;

export const getActiveRecordingSessions = async (
  projectId: ProjectId
): Promise<ActiveRecordingSession[]> => {
  const response = await gatewayFetch(
    `/api/v1/projects/${projectId}/recording_sessions/active`,
    {
      method: "GET",
    },
    { reportErrors: false }
  );

  if (!response.ok) {
    throw new Error(
      `Unable to fetch active recording sessions for project ${projectId}`
    );
  }

  return await response.json();
};

const wait = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

export const retryPromise = async (
  taskFn: () => Promise<void>,
  retries = 3,
  interval = 50,
  shouldAbortFn?: (error: unknown) => boolean
): Promise<void> => {
  try {
    await taskFn();
  } catch (error) {
    let shouldAbort = false;
    if (shouldAbortFn) {
      shouldAbort = shouldAbortFn(error);
    }

    if (retries <= 0 || shouldAbort) {
      return Promise.reject(error);
    }

    await wait(interval);

    return retryPromise(taskFn, retries - 1, interval);
  }
};

export const uploadRecordingSessionChunk = async (
  projectId: ProjectId,
  recordingSessionId: RecordingSessionId,
  sequenceId: number,
  chunk: File
): Promise<void> => {
  // If we receive a "410 Gone" status it means that the recording session has been finalized.
  // This can happen when the device wakes up after being suspended with a tab still open.
  // We should stop recording, and not attempt to upload any more chunks.
  const shouldAbort = (error: unknown): boolean => {
    return error instanceof DirectUploadGoneError;
  };

  return retryPromise(
    () =>
      uploadRecordingSessionChunkOnce(
        projectId,
        recordingSessionId,
        sequenceId,
        chunk
      ),
    5,
    100,
    shouldAbort
  );
};
export class DirectUploadGoneError extends UploadError {
  constructor(originalError: Error) {
    super(originalError);
    this.name = "DirectUploadGoneError";
  }
}

export const uploadRecordingSessionChunkOnce = async (
  projectId: ProjectId,
  recordingSessionId: RecordingSessionId,
  sequenceId: number,
  chunk: File
): Promise<void> => {
  const { signed_id: signedFileId } = await directUpload({
    fileOrBlob: chunk,
    folderPath: S3Folders.ProjectTracks,
    uploadContext: `recording-session:${recordingSessionId}`,
  });

  const response = await gatewayFetch(
    `/api/v1/projects/${projectId}/recording_sessions/${recordingSessionId}/chunks`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(
        mapToServerFormat({
          chunk: {
            signedFileId,
            sequenceId,
          },
        })
      ),
    },
    { reportErrors: false }
  );

  if (response.status !== 201) {
    throw Error(
      `uploadRecordingSessionChunk failed. Status: ${response.status}.`
    );
  }
};

export const recoverRecordingSession = async ({
  project,
  recordingSessionId,
}: {
  project: Project;
  recordingSessionId: RecordingSessionId;
}): Promise<void> => {
  const response = await gatewayFetch(
    `/api/v1/projects/${project.id}/recording_sessions/${recordingSessionId}/recover`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
    },
    { reportErrors: false }
  );

  if (response.status !== 201 && response.status !== 304) {
    throw Error(`recoverRecordingSession failed. Status: ${response.status}.`);
  }
};

export const recoverRecordingSessionTrack = async ({
  project,
  recordingSessionId,
  track,
}: {
  project: Project;
  recordingSessionId: RecordingSessionId;
  track: RecordingSessionTrack;
}): Promise<void> => {
  const response = await gatewayFetch(
    `/api/v1/projects/${project.id}/recording_sessions/${recordingSessionId}/tracks/${track.id}/recover`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
    },
    { reportErrors: false }
  );

  if (response.status !== 201) {
    throw Error(`recoverRecordingSession failed. Status: ${response.status}.`);
  }
};

interface UploadRecordingSessionArguments {
  project: Project;
  recordingSessionId: RecordingSessionId;
  duration: number;
  blob?: File;
}

interface UpdateRecordingSessionBody {
  duration: number;
  signedFileId?: string;
  anonymousAccessKey?: string;
}

export async function uploadRecordingSession({
  project,
  recordingSessionId,
  duration,
  blob,
}: UploadRecordingSessionArguments): Promise<unknown> {
  const data: UpdateRecordingSessionBody = {
    duration,
    anonymousAccessKey: project.anonymous_access_key ?? undefined,
  };

  if (blob) {
    const { signed_id: signedFileId } = await directUpload({
      fileOrBlob: blob,
      folderPath: S3Folders.ProjectTracks,
      uploadContext: `recording-session:${recordingSessionId}`,
    });
    data.signedFileId = signedFileId;
  }

  const response = await gatewayFetch(
    `/api/v1/projects/${project.id}/recording_sessions/${recordingSessionId}`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(mapToServerFormat(data)),
    },
    { reportErrors: false }
  );

  if (response.ok) {
    return await response.json();
  } else {
    throw Error(`uploadRecordingSession failed. Status: ${response.status}.`);
  }
}

export const updateRecordingSessionTrack = async (
  projectId: ProjectId,
  recordingSessionId: RecordingSessionId,
  trackId: RecordingSessionTrackId,
  updates: Partial<RecordingSessionTrack>
): Promise<RecordingSessionTrack> => {
  const response = await gatewayFetch(
    `/api/v1/projects/${projectId}/recording_sessions/${recordingSessionId}/tracks/${trackId}`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(mapToServerFormat(updates)),
    },
    { reportErrors: false }
  );

  if (response.ok) {
    return await response.json();
  } else {
    throw Error(
      `updateRecordingSessionTrack failed. Status: ${response.status}.`
    );
  }
};

export const saveTranscript = async (
  projectId: ProjectId,
  assetId: AssetId,
  transcript: AssetTranscript
): Promise<Response> => {
  const serverTranscript = removeIdsFromTranscript(transcript).map(
    (segment) => ({
      ...segment,
      words: segment.words.map((word) => mapToServerFormat(word)),
    })
  );

  return await gatewayFetch(
    `/api/v1/projects/${projectId}/tracks/${assetId}/transcript`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ transcript: serverTranscript }),
    },
    { reportErrors: false } // Callers are handling error and or reporting
  );
};

interface CreateTrackParticipantProps {
  projectId: ProjectId;
  assetId: AssetId;
  data: Partial<AssetParticipant>;
}

interface UpdateTrackParticipantProps extends CreateTrackParticipantProps {
  trackParticipantId: string;
}

// This is used to override speaker display names or avatars
export const createTrackParticipant = async ({
  projectId,
  assetId,
  data,
}: CreateTrackParticipantProps): Promise<string> => {
  const response = await gatewayFetch(
    `/api/v1/projects/${projectId}/tracks/${assetId}/participants`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(mapToServerFormat(data)),
    }
  );
  if (response.ok) {
    const responseJson = await response.json();
    return responseJson.id;
  }

  throw new UnexpectedHttpResponseError(
    `Failed to create track participant - received non-2xx status`,
    response.status,
    response.statusText
  );
};

// This is used to override speaker display names or avatars
export const updateTrackParticipant = async ({
  projectId,
  assetId,
  trackParticipantId,
  data,
}: UpdateTrackParticipantProps): Promise<string> => {
  const response = await gatewayFetch(
    `/api/v1/projects/${projectId}/tracks/${assetId}/participants/${trackParticipantId}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(mapToServerFormat(data)),
    }
  );
  if (response.ok) {
    const responseJson = await response.json();
    return responseJson.id;
  }

  throw new UnexpectedHttpResponseError(
    `Failed to update track participant - received non-2xx status`,
    response.status,
    response.statusText
  );
};

export interface ProjectShare {
  success: boolean;
  code: number;
}

export const shareProject = async ({
  project,
  email,
}: {
  project: Project;
  email: string;
}): Promise<ProjectShare> => {
  const formData = new FormData();
  formData.append("email", email);

  const rawResponse = await gatewayFetch(
    `/api/v1/projects/${project.id}/shares`,
    {
      method: "POST",
      body: formData,
    },
    { reportErrors: false }
  );

  // We want to show why it's a 403 so returning that to view
  if (rawResponse.status === 201 || rawResponse.status === 403) {
    return (await rawResponse.json()) as ProjectShare;
  }

  throw Error(`Project invite request failed. ${rawResponse.status}`);
};

export const uninvite = async ({
  project,
  email,
}: {
  project: Project;
  email: string;
}): Promise<void> => {
  const formData = new FormData();
  formData.append("email", email);

  const rawResponse = await gatewayFetch(
    `/api/v1/projects/${project.id}/shares`,
    {
      method: "DELETE",
      body: formData,
    },
    { reportErrors: false }
  );

  if (rawResponse.status != 200) {
    throw Error(`Uninvite request failed. ${rawResponse.status}`);
  }
};

export interface AcceptedInviteData {
  displayName: string;
  email: string;
  userId: AdobeId;
}

export interface UnacceptedInviteData {
  email: string;
}

interface ProjectInviteesResponse {
  unaccepted: UnacceptedInviteData[]; // emails
  accepted: AcceptedInviteData[];
}

export const getProjectInvitees = async ({
  project,
}: {
  project: Project;
}): Promise<ProjectInviteesResponse> => {
  const rawResponse = await gatewayFetch(
    `/api/v1/projects/${project.id}/shares`,
    {
      method: "GET",
    }
  );

  return await rawResponse.json();
};

export const createAnonymousAccessKey = async (
  project: Project
): Promise<string> => {
  const response = await gatewayFetch(
    `/api/v1/projects/${project.id}/anonymous_access_keys`,
    { method: "POST" },
    { reportErrors: false }
  );

  if (response.ok) {
    return mapFromServerFormat(await response.json()).anonymousAccessKey;
  } else {
    throw `error creating anonymous session ${response.status}`;
  }
};

export const revokeAnonymousAccessKey = async (
  project: Project
): Promise<void> => {
  const response = await gatewayFetch(
    `/api/v1/projects/${project.id}/anonymous_access_keys/${project.anonymous_access_key}`,
    { method: "DELETE" },
    { reportErrors: false }
  );

  if (!response.ok) {
    throw `error revoking anonymous session ${response.status}`;
  }
};

export const convertProjectToTemplate = async ({
  project,
}: {
  project: Project;
}): Promise<unknown> => {
  const data = { project_id: project.id };

  const rawResponse = await gatewayFetch(`/api/v1/templates/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });

  return await rawResponse.json();
};

export const deleteAssociatedTemplate = async ({
  project,
}: {
  project: Project;
}): Promise<void> => {
  const templateId = project.source_template_id;
  await gatewayFetch(`/api/v1/templates/${templateId}`, {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
  });
};

export const deleteProject = async (projectId: ProjectId): Promise<void> => {
  const response = await gatewayFetch(`/api/v1/projects/${projectId}`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
  });

  if (!response.ok) {
    throw Error(`Project deletion failed. Status: ${response.status}`);
  }
};

export const duplicateProject = async (
  projectId: ProjectId
): Promise<Project> => {
  const rawResponse = await gatewayFetch(
    `/api/v1/projects/${projectId}/duplicate`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    }
  );
  return (await rawResponse.json()) as Project;
};

export class ProjectUnauthorizedError extends Error {
  constructor() {
    super("");
    this.name = "ProjectUnauthorizedError";
  }
}

export type TemplateId = string;

export const cloneTemplate = async (
  templateId: TemplateId
): Promise<Project> => {
  const response = await gatewayFetch(
    `/api/v1/templates/${templateId}/clone`,
    {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    },
    { reportErrors: false }
  );

  if (response.status === 201) {
    return await response.json();
  } else if (response.status === 403) {
    throw new ProjectUnauthorizedError();
  } else {
    throw new Error(`Failed to clone template. Status: ${response.status}`);
  }
};

export const createProject = async (): Promise<Project> => {
  const response = await gatewayFetch(
    `/api/v1/projects`,
    {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    },
    { reportErrors: false }
  );

  if (response.status === 201) {
    return await response.json();
  } else if (response.status === 403) {
    throw new ProjectUnauthorizedError();
  } else {
    throw new Error(`Failed to create project. Status: ${response.status}`);
  }
};

export const getProjects = async (): Promise<Project[]> => {
  const response = await gatewayFetch(
    `/api/v1/projects`,
    {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    },
    { reportErrors: false }
  );

  if (response.status === 200) {
    return await response.json();
  } else if (response.status === 403) {
    throw new ProjectUnauthorizedError();
  } else {
    throw new Error(`Failed to fetch projects. Status: ${response.status}`);
  }
};

export const S3Folders = {
  ProjectTracks: "ProjectTracks",
  QuickActions: "QuickActionJobs",
  MusicCollectionTracks: "MusicCollectionTracks",
  RecordingSessionImports: "RecordingSessionImports",
  AudiogramCustomBackgrounds: "AudiogramCustomBackgrounds",
};

export const computeTrackWaveform = async (
  project: Project,
  assetId: AssetId
): Promise<void> => {
  const response = await gatewayFetch(
    `/api/v1/projects/${project.id}/tracks/${assetId}/waveform`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
    }
  );

  if (!response.ok) {
    throw new UnexpectedHttpResponseError(
      "Failed to compute a track waveform",
      response.status,
      response.statusText
    );
  }
};
